import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import DeliverySvg from '@/components/common/DeliverySvg';
import PickupSvg from '@/components/common/PickupSvg';
import ShippingTruckSvg from '@/components/common/ShippingTruckSvg';
import { TmCartLine, TmCartMessageType } from '@/tscapi/model/cart';
import { TmProduct } from '@/tscapi/model/product';

import Favorite from './Favorite';
import { en, fr } from './i18n';
import { QuantitySelector } from './QuantitySelector';

type CartLineProps = {
    CartLine: TmCartLine;
    CartProducts: TmProduct[];
    CartMessages?: TmCartMessageType;
    IsPickup?: boolean;
    isModifyOrder: boolean;
    Remove: () => void;
    Update: any;
    containerCss?: string;
    hideQty?: boolean;
    SellerName?: string;
    Promocode: string;
};

export const ItemCard = ({
    CartLine,
    CartProducts,
    CartMessages,
    IsPickup,
    isModifyOrder,
    Remove,
    Update,
    containerCss,
    hideQty,
    Promocode,
}: CartLineProps) => {
    const { i18n, t } = useTranslation('shoppingbag');

    useEffect(() => {
        i18n.addResourceBundle('en', 'shoppingbag', en, true, true);
        i18n.addResourceBundle('fr', 'shoppingbag', fr, true, true);
    }, [i18n]);

    const message = CartLine?.InCart?.UpsellMessage?.trim();
    const isUpsellUnlocked = Boolean(message && message.toLowerCase().startsWith('deal'));
    const product = CartProducts.find((x) => x.ItemNo === CartLine.CartLineItem.ItemNo);
    const edpData = product?.Edps.find((y) => y.EdpNo === CartLine.CartLineItem.EdpNo);
    const inventory = CartLine.CartLineItem.Inventory;

    return (
        <>
            {CartLine?.InCart?.UpsellMessage && (
                <span
                    style={{ borderColor: isUpsellUnlocked ? '#2a979d' : '#ff0000' }}
                    className="mb-2.5 inline-flex items-center border-2 border-solid px-2 py-1"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 15 15"
                        width="15"
                        height="15"
                        className="mr-1.5 inline-block"
                        fill={isUpsellUnlocked ? '#2a979d' : '#ff0000'}
                    >
                        <path d="M14.52 8.27 7.55 1.3a.543.543 0 0 0-.34-.16L1.73.64C1.11.58.59 1.1.64 1.73l.5 5.48c.01.13.07.25.16.34l6.97 6.97c.22.22.57.22.78 0l5.47-5.47c.22-.22.22-.57 0-.78ZM3.81 3.81c-.44.44-1.15.44-1.59 0-.44-.44-.44-1.15 0-1.59.44-.44 1.15-.44 1.59 0s.44 1.15 0 1.59Z" />
                    </svg>
                    <span className="text-xsm font-semibold leading-4.5">
                        <span className={!isUpsellUnlocked ? 'pr-2' : ''}>{CartLine.InCart.UpsellMessage}</span>
                        {!isUpsellUnlocked && (
                            <a
                                href="/pages/purchasewithpurchase"
                                className="whitespace-nowrap text-xsm underline hover:font-bold hover:underline"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Learn More
                            </a>
                        )}
                    </span>
                </span>
            )}
            <div className={containerCss}>
                <div className="item__image">
                    {CartLine.InCart.IsEditable ? (
                        <a href={product?.ProductLink?.LinkWithoutDomain}>
                            <img
                                className="img-responsive"
                                width="175"
                                height="175"
                                alt=""
                                src={`https://item.tscimg.ca/TSC/${CartLine.CartLineItem.ImageUrl}?impolicy=M`}
                            />
                        </a>
                    ) : (
                        <img
                            className="img-responsive"
                            width="175"
                            height="175"
                            alt=""
                            src={`https://item.tscimg.ca/TSC/${CartLine.CartLineItem.ImageUrl}?impolicy=M`}
                        />
                    )}
                </div>
                <div className="item__description">
                    {isModifyOrder && <div className="item__newly-added">{t('NEWLY ADDED')}</div>}
                    {product?.PriceIsLabel && <div className="item__badge">{product.PriceIsLabel}</div>}

                    {CartLine.InCart.IsEditable ? (
                        <a href={product?.ProductLink?.LinkWithoutDomain}>
                            <h3 className="item__name">{product?.Name}</h3>
                        </a>
                    ) : (
                        <h3 className="item__name">{product?.Name}</h3>
                    )}

                    <div className="item__number">
                        {product?.ItemNo.slice(0, 3) + '-' + product?.ItemNo.slice(3, 6)}
                    </div>

                    {(CartLine.CartLineItem.Size || CartLine.CartLineItem.Style) && (
                        <div className="item__style">
                            {CartLine.CartLineItem.Size && (
                                <span>
                                    {t('Size')}: {CartLine.CartLineItem.Size}
                                </span>
                            )}
                            {CartLine.CartLineItem.Size && CartLine.CartLineItem.Style && (
                                <span className="pipe-char">|</span>
                            )}
                            {CartLine.CartLineItem.Style && (
                                <span>
                                    {t('Style')}: {CartLine.CartLineItem.Style}
                                </span>
                            )}
                        </div>
                    )}
                    {product?.SellerType === 'Marketplace' && (
                        <div className="seller__type">
                            <img
                                src="/App_Assets/public/marketplace-icon.svg"
                                alt="marketplace-icon"
                                className="marketplace__icon"
                            />
                            <div className="marketplace__text">Marketplace Seller</div>
                        </div>
                    )}
                    {/* quantity display if quantity selector disabled */}
                    {hideQty && (
                        <div className="item__style" style={{ marginBottom: '10px' }}>
                            Quantity: {CartLine?.InCart?.Quantity}
                        </div>
                    )}

                    {/* low stock */}
                    {inventory < 10 && inventory > 0 && (
                        <div className="item__stock-level--urgent">Only {inventory} left!</div>
                    )}
                </div>

                {CartLine.InCart.IsEditable && (
                    <div className="item__fav">
                        <Favorite product={product} edp={edpData} />
                    </div>
                )}

                {CartLine.InCart.IsEditable && !hideQty && (
                    <div className="item__qty">
                        <QuantitySelector
                            value={CartLine.InCart.Quantity}
                            inventory={inventory}
                            lineId={CartLine.Id}
                            edpNo={CartLine.CartLineItem.EdpNo}
                            update={Update}
                        />
                        <button type="button" className="item__remove" onClick={Remove}>
                            {t('Remove')}
                        </button>
                    </div>
                )}

                <div className="item__price">
                    <div>
                        <div style={{ textAlign: 'right' }}>
                            <span className="item__is-price">{CartLine.InCart.UnitPrice}</span>
                            {CartLine.InCart.WasPrice && (
                                <span className="item__was-price">{CartLine.InCart.WasPrice}</span>
                            )}
                        </div>

                        {Promocode && Promocode !== 'FREESHIP' && (
                            <div className="item__promocode">
                                {CartLine.InCart.IsPromoEligible ? (
                                    CartLine.InCart.Discount ? (
                                        <div>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 15 15"
                                                width="15"
                                                height="15"
                                                className="upsell-icon"
                                                fill={'#01b9bb'}
                                            >
                                                <path d="M14.52 8.27 7.55 1.3a.543.543 0 0 0-.34-.16L1.73.64C1.11.58.59 1.1.64 1.73l.5 5.48c.01.13.07.25.16.34l6.97 6.97c.22.22.57.22.78 0l5.47-5.47c.22-.22.22-.57 0-.78ZM3.81 3.81c-.44.44-1.15.44-1.59 0-.44-.44-.44-1.15 0-1.59.44-.44 1.15-.44 1.59 0s.44 1.15 0 1.59Z" />
                                            </svg>
                                            <span className="upsell-text">
                                                {Promocode} (-{CartLine.InCart.Discount})
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="upsell-text">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 15 15"
                                                width="15"
                                                height="15"
                                                className="upsell-icon"
                                                fill={'#01b9bb'}
                                            >
                                                <path d="M14.52 8.27 7.55 1.3a.543.543 0 0 0-.34-.16L1.73.64C1.11.58.59 1.1.64 1.73l.5 5.48c.01.13.07.25.16.34l6.97 6.97c.22.22.57.22.78 0l5.47-5.47c.22-.22.22-.57 0-.78ZM3.81 3.81c-.44.44-1.15.44-1.59 0-.44-.44-.44-1.15 0-1.59.44-.44 1.15-.44 1.59 0s.44 1.15 0 1.59Z" />
                                            </svg>
                                            <span>Eligible for {Promocode}</span>
                                        </div>
                                    )
                                ) : (
                                    <div className="item__excluded">
                                        <span>Excluded from promo</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {CartLine.InCart.TaxLabel && (
                            <div className="item__taxlabel">
                                <span>{CartLine.InCart.TaxLabel}</span>
                            </div>
                        )}

                        {!isModifyOrder &&
                            inventory > 0 &&
                            (CartLine.InCart.EstimatedShipDate ||
                                CartLine.InCart.EstimatedDeliveryDate ||
                                CartMessages?.EstimatedDate) &&
                            CartLine.InCart.IsEditable && (
                                <div className="item__estimation">
                                    <div className="item__estimated-date">
                                        <div className="item__estimated-date--svg">
                                            {IsPickup ? (
                                                <PickupSvg className={'item__svg--pickup'} />
                                            ) : (
                                                <DeliverySvg className={'item__svg--delivery'} />
                                            )}
                                            {!CartLine.InCart.EstimatedDateLabel && CartMessages?.EstimatedDate && (
                                                <>
                                                    <span style={{ lineHeight: '20px' }}>
                                                        {CartMessages.EstimatedDateLabel}
                                                        {CartMessages.EstimatedDate && (
                                                            <strong style={{ whiteSpace: 'nowrap' }}>
                                                                {CartMessages.EstimatedDate}
                                                            </strong>
                                                        )}
                                                    </span>
                                                </>
                                            )}
                                            {CartLine.InCart.EstimatedDateLabel && (
                                                <>
                                                    <span style={{ lineHeight: '20px' }}>
                                                        {CartLine.InCart.EstimatedDateLabel}
                                                        {CartLine.InCart.EstimatedDeliveryDate && (
                                                            <strong style={{ whiteSpace: 'nowrap' }}>
                                                                {CartLine.InCart.EstimatedDeliveryDate}
                                                            </strong>
                                                        )}
                                                        {CartLine.InCart.EstimatedShipDate && (
                                                            <strong style={{ whiteSpace: 'nowrap' }}>
                                                                {CartLine.InCart.EstimatedShipDate}
                                                            </strong>
                                                        )}
                                                    </span>
                                                </>
                                            )}
                                        </div>

                                        {!(
                                            CartLine.InCart.IsFreeShippingEligible ||
                                            CartLine.InCart.ShippingHandling === 'FREE' ||
                                            CartLine.InCart.ShippingHandling === 'GRATUIT'
                                        ) && (
                                            <div className="item__shipping">
                                                <span className="item__estimated-shipping--label">
                                                    {t('Shipping')}:
                                                </span>
                                                <span className="item__estimated-shipping--is">
                                                    {CartLine.InCart.ShippingHandling}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                        {!CartLine.InCart.IsFreeShippingEligible && CartLine.InCart.ShippingHandling != 'FREE' && (
                            <div>
                                <span className="item__noteligible-freeship">
                                    Not eligible for free standard shipping
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};
